import React from "react";
import { minutesToHoursText } from "../utils/timeUnits";
import { parseDecimalInput } from "../utils/decimalInput";

// A phase that took far longer than planned is almost always a mistyped Почеток/Крај
// (e.g. „00:00“ instead of „12:00“ turns a 5-minute dismount into 12:40 h). Flag it so the
// operator sees it before it inflates Вк. РН. Planned 0 / missing → only absurd values (>12 h).
const OVERRUN_FACTOR = 3;
const OVERRUN_MIN_EXTRA = 60;
const NO_PLAN_LIMIT = 12 * 60;

// Minutes arrive as numbers or the table's decimal strings — same parser as the rest of the table.
export const isDurationOverrun = (actualValue: unknown, plannedValue: unknown): boolean => {
  const actual = parseDecimalInput(actualValue);
  if (actual === null || actual <= 0) return false;
  const plan = parseDecimalInput(plannedValue) ?? 0;
  if (plan <= 0) return actual > NO_PLAN_LIMIT;
  return actual > plan * OVERRUN_FACTOR && actual - plan > OVERRUN_MIN_EXTRA;
};

// Swap the cell's own background/text colour for the warning colours (no !important).
const withoutColours = (cls: string) =>
  cls.replace(/\b(?:bg|text)-(?:gray|white|slate)[\w/-]*/g, "").replace(/\s+/g, " ").trim();

interface Props {
  actual: unknown;
  planned: unknown;
  /** The cell's normal classes (border, padding, alignment, background). */
  className: string;
}

/** RN „… РН“ duration cell of the Log Book: the actual time, red when it far exceeds the plan. */
const LogBookRnDurationCell: React.FC<Props> = ({ actual, planned, className }) => {
  const overrun = isDurationOverrun(actual, planned);
  return (
    <td
      className={overrun ? `${withoutColours(className)} bg-red-50 text-red-700 font-semibold` : className}
      title={overrun
        ? `Многу подолго од планираното (план ${minutesToHoursText(planned) || "0:00"}) — провери Почеток/Крај`
        : undefined}
    >
      {minutesToHoursText(actual)}
    </td>
  );
};

export default LogBookRnDurationCell;
